import React from 'react';
import { HTTP, ScreenName } from '@helpcenter/constants';
import { ErrorPage } from '@helpcenter/error-page';
import type { RouteContext } from 'react-resource-router';
import { useRouter } from 'react-resource-router';
import { ContextSetter } from 'view/common/context';
import { ScreenErrorBoundary } from 'view/layout/screen-error-boundary';
import { RequestCreateAnonymousSuccess } from 'view/request-create-anonymous-success';
import { RequestCreatePage } from 'view/request-create-page';
import { RequestCreatePageForHubs } from 'view/request-create-page/hub-variant';
import { RequestFeedback } from 'view/request-feedback';
import { Unsubscribe } from 'view/unsubscribe';
import { UserProfile } from 'view/user-profile';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next/withAnalyticsEvents';
import withAnalyticsContext from '@atlaskit/analytics-next/withAnalyticsContext';
import withAnalyticsEvents from '@atlaskit/analytics-next/withAnalyticsEvents';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

import { UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { AnalyticsScreen } from '@atlassian/help-center-common-util/analytics/analytics-screen';
import { createRouteWithLayout } from '@atlassian/help-center-common-util/resource-router';
import { UiModificationsContainer } from '@atlassian/jsm-ui-modifications-view-request-create/src/ui/container';
export { portalReorderRoute } from 'routes/portal-reorder';

const HUBS_REQUEST_CREATE_KB_SUGGESTIONS = 'hubs_request_create_kb_suggestions';
const isHubsRequestCreateKbSuggestionsEnabled = (): boolean =>
	FeatureGates.checkGate(HUBS_REQUEST_CREATE_KB_SUGGESTIONS);

const portalRoutePrefix = '/portal/:portalId(\\d+)';

export const RenderRequestCreate = () => {
	const [routerState] = useRouter();

	return (
		<AnalyticsScreen screenName={ScreenName.REQUEST_CREATE} screenId={routerState.match.url}>
			<ScreenErrorBoundary screenName={ScreenName.REQUEST_CREATE}>
				<UiModificationsContainer>
					<RequestCreatePage {...routerState} />
				</UiModificationsContainer>
			</ScreenErrorBoundary>
		</AnalyticsScreen>
	);
};

export const RenderRequestCreateForHubs = () => {
	const [routerState] = useRouter();
	return (
		<AnalyticsScreen screenName={ScreenName.REQUEST_CREATE} screenId={routerState.match.url}>
			<ScreenErrorBoundary screenName={ScreenName.REQUEST_CREATE}>
				<UiModificationsContainer>
					<RequestCreatePageForHubs {...routerState} />
				</UiModificationsContainer>
			</ScreenErrorBoundary>
			{/* Hub routes skip createRouteWithLayout, so set ui.context here: the reused request
			    create views (e.g. KB suggestions on the summary field) read getPortalId(state). */}
			{isHubsRequestCreateKbSuggestionsEnabled() && (
				<ContextSetter
					match={routerState.match}
					location={routerState.location}
					route={routerState.route}
				/>
			)}
		</AnalyticsScreen>
	);
};

export const RenderCreateAnonymousSuccess = () => {
	const [routerState] = useRouter();
	const { basePath, ...routeContext } = routerState;
	return (
		<AnalyticsScreen
			screenName={ScreenName.REQUEST_CREATE_ANONYMOUS_SUCCESS}
			screenId={routeContext.match.url}
		>
			<ScreenErrorBoundary screenName={ScreenName.REQUEST_CREATE_ANONYMOUS_SUCCESS}>
				<RequestCreateAnonymousSuccess {...routeContext} />
			</ScreenErrorBoundary>
		</AnalyticsScreen>
	);
};

export const userProfileRoute = createRouteWithLayout(
	{
		name: 'user-profile',
		path: '/user/profile',
		exact: true,
		resources: [],
		component: UserProfile,
	},
	ScreenName.USER_PROFILE,
	{
		hideSearchButton: false,
		paperAppearance: 'normal',
		fadeInSearchModal: true,
	},
);
export const NotFoundPage: React.FC<WithAnalyticsEventsProps & RouteContext> = ({
	createAnalyticsEvent,
}) => {
	createAnalyticsEvent &&
		createAnalyticsEvent({
			action: 'rendered',
			analyticsType: UI_EVENT_TYPE,
		}).fire();

	return (
		<ErrorPage
			error={{
				status: HTTP.NOT_FOUND,
				message: '',
				callToActionUrl: '',
				callToActionText: '',
			}}
		>
			{/*This is needed because the ErrorPage requires children props.*/}
			{''}
		</ErrorPage>
	);
};

export const getRequestCreateRoute = () =>
	createRouteWithLayout(
		{
			name: 'request-create',
			path: '/portal/:portalId/create/:requestTypeId',
			exact: true,
			component: RenderRequestCreate,
			resources: [],
		},
		ScreenName.REQUEST_CREATE,
		{
			fadeInSearchModal: true,
			paperAppearance: 'xxwide',
		},
	);

export const anonymousRequestSuccessRoute = createRouteWithLayout(
	{
		name: 'request-create-anonymous-success',
		path: '/portal/:portalId/create/:requestTypeId/confirm',
		exact: true,
		component: RenderCreateAnonymousSuccess,
		resources: [],
	},
	ScreenName.KNOWLEDGE_CATEGORY,
	{
		fadeInSearchModal: true,
	},
);

export const requestFeedbackRoute = createRouteWithLayout(
	{
		name: 'request-feedback',
		path: `${portalRoutePrefix}/:requestKey/feedback`,
		exact: true,
		component: RequestFeedback,
		resources: [],
	},
	ScreenName.FEEDBACK,
	{
		hideSearchButton: true,
		paperAppearance: 'note',
		fadeInSearchModal: true,
	},
);

export const helpCenterRequestFeedbackRoute = createRouteWithLayout(
	{
		name: 'request-feedback',
		path: `/user/requests/:requestKey/feedback`,
		exact: true,
		component: RequestFeedback,
		resources: [],
	},
	ScreenName.FEEDBACK,
	{
		hideSearchButton: true,
		paperAppearance: 'note',
		fadeInSearchModal: true,
	},
);

export const unsubscribeRoute = createRouteWithLayout(
	{
		name: 'unsubscribeRoute',
		path: `${portalRoutePrefix}/:requestKey/unsubscribe`,
		exact: true,
		component: Unsubscribe,
		resources: [],
	},
	ScreenName.UNSUBSCRIBE,
	{
		hideSearchButton: true,
		paperAppearance: 'note',
		fadeInSearchModal: true,
	},
);

export const notFoundErrorRoute = createRouteWithLayout(
	{
		name: 'not-found-error-page',
		path: '(.*)',
		exact: true,
		component: withAnalyticsContext({ componentName: 'notFoundPage' })(
			withAnalyticsEvents({})(NotFoundPage),
		),
		resources: [],
	},
	ScreenName.ERROR_NOT_FOUND,
	{
		paperAppearance: 'normal',
		fadeInSearchModal: true,
	},
);
